import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { LineChart, Line, AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, ReferenceLine, ReferenceDot } from 'recharts';
import { V } from '../utils/theme';
import { LS } from '../utils/storage';
import { Icons } from '../components/Icons';
import { track, EmptyState, SectionTitle } from '../components/kit';
import { useLayout } from '../utils/responsive';
import { Card, Btn, Chip, Sheet, Stat, Progress } from '../components/ui';
import { today, ago, fmtShort, calc1RM, convW, wUnit, isCardio, chartCfg, dUnit , liftsIn } from '../utils/helpers';
import { ShareCard } from '../utils/share';
import { ALL_MUSCLES, MUSCLE_MAP, MUSCLE_LABELS } from './features';

export function getOverloadSuggestion(exerciseId, workouts, units){
  // Find last 3 sessions with this exercise
  const sessions=[];
  for(const w of workouts){
    const ex=w.exercises.find(e=>e.exerciseId===exerciseId);
    if(ex&&ex.sets.some(s=>s.weight>0)){sessions.push({date:w.date,sets:ex.sets});if(sessions.length>=3)break;}
  }
  if(sessions.length===0)return null;

  const latest=sessions[0];
  const bestSet=latest.sets.reduce((m,st)=>parseFloat(st.weight)>parseFloat(m.weight)?st:m,{weight:0,reps:0});
  const w=parseFloat(bestSet.weight)||0;
  const r=parseInt(bestSet.reps)||0;
  if(w===0)return null;

  const e1rm=Math.round(w*(1+r/30));
  const inc=units==="kg"?2.5:5;
  const targetReps=8;

  // Check if all sets hit target reps
  const allSetsHit=latest.sets.every(st=>parseInt(st.reps)>=targetReps);
  // Check for failure pattern (last 2-3 sessions struggling)
  const failCount=sessions.filter(sess=>{
    const bst=sess.sets.reduce((m,st)=>parseFloat(st.weight)>parseFloat(m.weight)?st:m,{weight:0,reps:0});
    return parseInt(bst.reps)<targetReps-2;
  }).length;

  let type,sugWeight,sugReps,reason;
  if(failCount>=2){
    // Deload
    sugWeight=Math.max(inc,Math.round((w-inc*2)/inc)*inc);
    sugReps=targetReps;
    type="deload";
    reason="Struggling multiple sessions — deload to rebuild";
  }else if(allSetsHit){
    // Increase weight
    sugWeight=w+inc;
    sugReps=r;
    type="increase";
    reason="All sets completed — increase weight";
  }else if(r<targetReps-1){
    // Keep same, failed reps
    sugWeight=w;
    sugReps=targetReps;
    type="repeat";
    reason="Missed reps — repeat and aim higher";
  }else{
    // Keep same, almost there
    sugWeight=w;
    sugReps=r+1;
    type="reps";
    reason="Close to target — add a rep";
  }

  const zone80=Math.round(e1rm*0.80);
  const zone70=Math.round(e1rm*0.70);

  return{type,last:`${w}×${r}`,suggest:`${sugWeight}×${sugReps}`,e1rm,zone80,zone70,reason,
    allSets:latest.sets.map(st=>`${st.weight}×${st.reps}`).join("  "),date:sessions[0].date};
}

// ─── Muscle Heat Map Calculator ───
export function calcMuscleHeat(workouts,exercises,days=10){
  const now=new Date();
  const scores={};
  ALL_MUSCLES.forEach(m=>{scores[m]=0;});

  workouts.forEach(w=>{
    const daysAgo=Math.max(0,(now-new Date(w.date))/(1000*60*60*24));
    if(daysAgo>days)return;
    const decay=Math.max(0,1-daysAgo/days);

    w.exercises.forEach(ex=>{
      const map=MUSCLE_MAP[ex.exerciseId];
      if(!map)return;
      const setCount=ex.sets.length;
      const volume=ex.sets.reduce((s,st)=>(parseFloat(st.weight)||0)*(parseInt(st.reps)||0)+s,0);
      const intensity=Math.min(1,volume/5000)+setCount*0.3;

      map.primary.forEach(m=>{scores[m]+=(intensity*3)*decay;});
      map.secondary.forEach(m=>{scores[m]+=(intensity*1.5)*decay;});
    });
  });

  // Normalize 0-10
  const mx=Math.max(...Object.values(scores),1);
  const norm={};
  ALL_MUSCLES.forEach(m=>{norm[m]=Math.min(10,Math.round(scores[m]/mx*10*10)/10);});
  return norm;
}

export function getMuscleColor(score){
  if(score<=0.5)return track();
  if(score<=3)return"rgba(250,204,21,0.4)";   // yellow
  if(score<=6)return"rgba(0,245,160,0.5)";     // green
  if(score<=8)return"rgba(0,245,160,0.8)";     // bright green
  return"rgba(255,107,107,0.7)";                // red (high fatigue)
}

export function getMuscleLabel(score){
  if(score<=0.5)return"Untrained";
  if(score<=3)return"Light";
  if(score<=6)return"Moderate";
  if(score<=8)return"Well Trained";
  return"High Volume";
}

// ─── Muscle Heat Map SVG Component ───
// ─── Volume Per Muscle Group ───
// ─── Exercise Progression Chart ───
export function ExerciseChart({s,d}){
  const { isDesktop } = useLayout();
  const [selEx,setSelEx]=useState(()=>{const pick=LS.get("ft-sel-exercise");if(pick){LS.set("ft-sel-exercise",null);return pick;}const logged=new Set((s.workouts||[]).flatMap(w=>w.exercises.map(e=>e.exerciseId)));return s.exercises.find(e=>logged.has(e.id))?.id||s.exercises[0]?.id||"bench";});
  const data=useMemo(()=>{
    const pts=[];
    s.workouts.filter(w=>w.date>=ago(s.range)).sort((a,b)=>a.date.localeCompare(b.date)).forEach(w=>{
      const ex=w.exercises.find(e=>e.exerciseId===selEx);
      if(ex){
        const best=ex.sets.reduce((m,st)=>parseFloat(st.weight)>m?parseFloat(st.weight):m,0);
        const topSet=ex.sets.reduce((m,st)=>parseFloat(st.weight)>parseFloat(m.weight)?st:m,{weight:0,reps:0});
        const vol=ex.sets.reduce((a,st)=>(parseFloat(st.weight)||0)*(parseInt(st.reps)||0)+a,0);
        if(best>0)pts.push({date:fmtShort(w.date),weight:best,reps:parseInt(topSet.reps)||0,
          e1rm:calc1RM(parseFloat(topSet.weight)||0,parseInt(topSet.reps)||0),vol,sets:ex.sets.length});
      }
    });
    return pts;
  },[selEx,s.workouts,s.range]);

  const exInfo=s.exercises.find(e=>e.id===selEx);
  // Active strength goal for this lift (from Plan → Goals) drawn as a target line
  const goal=useMemo(()=>{
    const name=(exInfo?.name||"").toLowerCase(), id=String(selEx||"").toLowerCase();
    return (s.milestones||[]).find(g=>g.type==="strength"&&!g.completed&&g.target>0&&(()=>{const l=(g.label||"").toLowerCase();const key=["bench","squat","deadlift","ohp","press","row","curl","pull"].find(k=>l.includes(k));return key&&(id.includes(key)||name.includes(key));})())||null;
  },[s.milestones,selEx,exInfo?.name]);
  const cats=["All",...new Set(s.exercises.map(e=>e.cat))];
  const [catF,setCatF]=useState("All");
  const filtered=catF==="All"?s.exercises:s.exercises.filter(e=>e.cat===catF);
  // Only exercises the user has actually logged
  const usedExIds=new Set();
  s.workouts.forEach(w=>w.exercises.forEach(e=>usedExIds.add(e.exerciseId)));
  const usedExercises=filtered.filter(e=>usedExIds.has(e.id));

  return(
    <div style={{display:"flex",flexDirection:"column",gap:12,maxWidth:isDesktop?900:"100%",margin:isDesktop?"0 auto":0}}>
      <div style={{fontSize:16,fontWeight:800,color:V.text}}>Exercise Progression</div>
      <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
        {cats.map(c=><Chip key={c} label={c} active={catF===c} onClick={()=>setCatF(c)}/>)}
      </div>
      <select value={selEx} onChange={e=>setSelEx(e.target.value)}
        style={{padding:"10px 14px",background:V.bg,border:`1px solid ${V.cardBorder}`,borderRadius:10,
          color:V.text,fontSize:13,outline:"none",fontFamily:V.font}}>
        {usedExercises.map(e=><option key={e.id} value={e.id}>{e.name}</option>)}
      </select>

      {data.length>1?(
        <Card style={{padding:12}}>
          <div style={{fontSize:11,fontWeight:700,color:V.text,marginBottom:8}}>{exInfo?.name} — Weight Over Time</div>
          <ResponsiveContainer width="100%" height={180}>
            <LineChart data={data}>
              <CartesianGrid {...chartCfg.grid} vertical={false}/>
              <XAxis dataKey="date" {...chartCfg.axis} interval="preserveStartEnd"/>
              <YAxis {...chartCfg.axis} width={32}/>
              <Tooltip {...chartCfg.tip}/>
              <Line type="monotone" dataKey="weight" stroke={V.accent} strokeWidth={2} dot={{r:3,fill:V.accent}}/>
              <Line type="monotone" dataKey="e1rm" stroke={V.purple} strokeWidth={1.5} strokeDasharray="4 4" dot={false}/>
              {goal&&<ReferenceLine y={goal.target} stroke={V.warn} strokeDasharray="6 4" label={{value:`Goal ${goal.target}`,position:"insideTopRight",fill:V.warn,fontSize:11,fontWeight:700}}/>}
            </LineChart>
          </ResponsiveContainer>
          <div style={{display:"flex",gap:12,justifyContent:"center",marginTop:6}}>
            <span style={{fontSize:11,color:V.accent}}>■ Top Weight</span>
            <span style={{fontSize:11,color:V.purple}}>- - E1RM</span>
          </div>
        </Card>
      ):<EmptyState icon={Icons.chart} color={V.purple} title="Need two sessions to chart" action={d?"Log a workout":null} onAction={()=>d&&d({type:"TAB",tab:"log_workout"})}>Log {exInfo?.name||"this exercise"} again and the progression line appears here.</EmptyState>}

      {data.length>0&&(
        <Card style={{padding:12}}>
          <div style={{fontSize:11,fontWeight:700,color:V.text,marginBottom:6}}>Session History</div>
          {data.slice().reverse().slice(0,10).map((d2,i)=>(
            <div key={i} style={{display:"flex",justifyContent:"space-between",padding:"5px 0",borderBottom:`1px solid ${V.divider}`,fontSize:11}}>
              <span style={{color:V.text3}}>{d2.date}</span>
              <span style={{fontFamily:V.mono,color:V.accent}}>{d2.weight}×{d2.reps}</span>
              <span style={{fontFamily:V.mono,color:V.purple}}>E1RM:{d2.e1rm}</span>
              <span style={{color:V.text3}}>{d2.sets}s · {d2.vol>1000?`${(d2.vol/1000).toFixed(1)}k`:d2.vol}vol</span>
            </div>
          ))}
        </Card>
      )}
    </div>
  );
}

// ─── Workout Duration Trends ───
export function DurationTrends({s,d}){
  const { isDesktop } = useLayout();
  const data=useMemo(()=>{
    return s.workouts.filter(w=>w.date>=ago(s.range)&&w.dur>0).sort((a,b)=>a.date.localeCompare(b.date))
      .map(w=>({date:fmtShort(w.date),dur:w.dur,sets:w.exercises.reduce((a,e)=>a+e.sets.length,0),
        exercises:w.exercises.length}));
  },[s.workouts,s.range]);

  const avgDur=data.length?Math.round(data.reduce((a,d2)=>a+d2.dur,0)/data.length):0;
  const avgSets=data.length?Math.round(data.reduce((a,d2)=>a+d2.sets,0)/data.length):0;
  const longest=data.reduce((m,d2)=>d2.dur>m?d2.dur:m,0);
  const shortest=data.filter(d2=>d2.dur>0).reduce((m,d2)=>d2.dur<m?d2.dur:m,999);
  const trend=data.length>=2?data[data.length-1].dur-data[0].dur:0;

  return(
    <div style={{display:"flex",flexDirection:"column",gap:12,maxWidth:isDesktop?900:"100%",margin:isDesktop?"0 auto":0}}>
      <div style={{fontSize:16,fontWeight:800,color:V.text}}>Workout Duration</div>

      <Card style={{padding:14}}>
        <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:8,textAlign:"center"}}>
          <div><div style={{fontSize:18,fontWeight:800,color:V.accent,fontFamily:V.mono}}>{avgDur}</div><div style={{fontSize:11,color:V.text3}}>AVG MIN</div></div>
          <div><div style={{fontSize:18,fontWeight:800,color:V.warn,fontFamily:V.mono}}>{longest}</div><div style={{fontSize:11,color:V.text3}}>LONGEST</div></div>
          <div><div style={{fontSize:18,fontWeight:800,color:V.accent2,fontFamily:V.mono}}>{shortest<999?shortest:"—"}</div><div style={{fontSize:11,color:V.text3}}>SHORTEST</div></div>
          <div><div style={{fontSize:18,fontWeight:800,color:trend>0?V.warn:V.accent,fontFamily:V.mono}}>{trend>0?"+":""}{trend}</div><div style={{fontSize:11,color:V.text3}}>TREND</div></div>
        </div>
      </Card>

      <Card style={{padding:14}}>
        <div style={{fontSize:11,fontWeight:700,color:V.text,marginBottom:6}}>Sets per session: avg {avgSets}</div>
      </Card>

      {data.length>1?(
        <Card style={{padding:12}}>
          <div style={{fontSize:11,fontWeight:700,color:V.text,marginBottom:8}}>Duration Over Time (min)</div>
          <ResponsiveContainer width="100%" height={160}>
            <BarChart data={data} barSize={10}>
              <CartesianGrid {...chartCfg.grid} vertical={false}/>
              <XAxis dataKey="date" {...chartCfg.axis} interval="preserveStartEnd"/>
              <YAxis {...chartCfg.axis} width={28}/>
              <Tooltip {...chartCfg.tip}/>
              <ReferenceLine y={avgDur} stroke={V.accent} strokeDasharray="4 4" strokeOpacity={0.5}/>
              <Bar dataKey="dur" fill={V.purple} radius={[3,3,0,0]}/>
            </BarChart>
          </ResponsiveContainer>
          <div style={{fontSize:11,color:V.text3,textAlign:"center",marginTop:4}}>Dashed line = average ({avgDur}min)</div>
        </Card>
      ):<EmptyState icon={Icons.clock} color="#f472b6" title="No durations yet" action={d?"Log a workout":null} onAction={()=>d&&d({type:"TAB",tab:"log_workout"})}>The live timer records duration automatically when you log from the workout sheet.</EmptyState>}
    </div>
  );
}

export function VolumeTracker({s,d}){
  const { isDesktop } = useLayout();
  const data=useMemo(()=>{
    const weekWorkouts=s.workouts.filter(w=>w.date>=ago(s.range||7));
    const map={};
    // Map exercise categories to muscle groups
    const catToMuscle={Chest:"Chest",Back:"Back",Legs:"Legs",Shoulders:"Shoulders",Biceps:"Arms",Triceps:"Arms",
      Arms:"Arms",Core:"Core",Glutes:"Legs",Cardio:"Cardio"};
    weekWorkouts.forEach(w=>w.exercises.forEach(ex=>{
      const info=s.exercises.find(e=>e.id===ex.exerciseId);
      const muscle=catToMuscle[info?.cat]||info?.cat||"Other";
      if(!map[muscle])map[muscle]={sets:0,exercises:new Set(),workouts:new Set()};
      map[muscle].sets+=ex.sets.length;
      map[muscle].exercises.add(info?.name||ex.exerciseId);
      map[muscle].workouts.add(w.date);
    }));
    // Recommended ranges (weekly sets)
    const recs={Chest:{min:10,max:20},Back:{min:10,max:20},Legs:{min:10,max:20},Shoulders:{min:8,max:16},
      Arms:{min:8,max:16},Core:{min:6,max:12},Cardio:{min:2,max:5}};
    return Object.entries(map).map(([muscle,d])=>{
      const rec=recs[muscle]||{min:8,max:16};
      const status=d.sets<rec.min?"low":d.sets>rec.max?"high":"good";
      return{muscle,sets:d.sets,exercises:d.exercises.size,workouts:d.workouts.size,rec,status};
    }).sort((a,b)=>b.sets-a.sets);
  },[s.workouts,s.exercises]);

  const totalSets=data.reduce((a,d)=>a+d.sets,0);

  return(
    <div style={{display:"flex",flexDirection:"column",gap:12,maxWidth:isDesktop?900:"100%",margin:isDesktop?"0 auto":0}}>
      <div style={{fontSize:16,fontWeight:800,color:V.text}}>Volume</div>
      <div style={{fontSize:11,color:V.text3}}>Sets per muscle group · last {s.range||7} days · recommended weekly ranges shown</div>

      <Card style={{padding:14}}>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,textAlign:"center"}}>
          <div><div style={{fontSize:20,fontWeight:800,color:V.accent,fontFamily:V.mono}}>{totalSets}</div><div style={{fontSize:11,color:V.text3}}>TOTAL SETS</div></div>
          <div><div style={{fontSize:20,fontWeight:800,color:V.purple,fontFamily:V.mono}}>{data.length}</div><div style={{fontSize:11,color:V.text3}}>MUSCLES HIT</div></div>
          <div><div style={{fontSize:20,fontWeight:800,color:V.warn,fontFamily:V.mono}}>{data.filter(d=>d.status==="low").length}</div><div style={{fontSize:11,color:V.text3}}>BELOW MIN</div></div>
        </div>
      </Card>

      {data.map((d,i)=>(
        <Card key={i} style={{padding:12}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
            <div>
              <div style={{fontSize:13,fontWeight:700,color:V.text}}>{d.muscle}</div>
              <div style={{fontSize:11,color:V.text3}}>{d.exercises} exercises · {d.workouts} sessions</div>
            </div>
            <div style={{textAlign:"right"}}>
              <div style={{fontSize:18,fontWeight:800,color:d.status==="good"?V.accent:d.status==="low"?V.danger:V.warn,fontFamily:V.mono}}>{d.sets}</div>
              <div style={{fontSize:11,color:V.text3}}>{d.rec.min}-{d.rec.max} rec</div>
            </div>
          </div>
          <div style={{height:4,borderRadius:2,background:V.hoverBg}}>
            <div style={{height:"100%",borderRadius:2,transition:"width .3s",
              background:d.status==="good"?V.accent:d.status==="low"?V.danger:V.warn,
              width:`${Math.min(100,d.sets/d.rec.max*100)}%`}}/>
          </div>
          {d.status==="low"&&<div style={{fontSize:11,color:V.danger,marginTop:4}}>⚠️ Below minimum — add {d.rec.min-d.sets} more sets</div>}
          {d.status==="high"&&<div style={{fontSize:11,color:V.warn,marginTop:4}}>📈 Above recommended — watch for overtraining</div>}
          {d.status==="good"&&<div style={{fontSize:11,color:V.accent,marginTop:4}}>✅ In optimal range</div>}
        </Card>
      ))}
      {data.length===0&&<EmptyState icon={Icons.dumbbell} title="No sets in this range" action={d?"Log a workout":null} onAction={()=>d&&d({type:"TAB",tab:"log_workout"})}>Nothing logged in the last {s.range||7} days. Volume by muscle shows up as soon as you train.</EmptyState>}
    </div>
  );
}

// ─── Anatomical figure geometry for MuscleHeatMap ───
// Muscle bellies are drawn for the figure's right side (viewer's right) and the
// left side is mirrored around the canvas center line (x=110 on a 220-wide canvas).
const mirX=d=>{let i=0;return d.replace(/-?\d+(\.\d+)?/g,n=>{const v=parseFloat(n);return String(i++%2===0?Math.round((220-v)*10)/10:v);});};

// Shared body silhouette (front & back): head, angled arms with hands, torso, legs, feet.
const BODY_OUTLINE="M110,6 C120,6 127,13 127,25 C127,36 123,45 117,49 C116,53 116,57 117,60 C130,63 143,68 153,75 C161,81 166,90 168,100 C170,112 172,124 174,137 C176,151 179,163 182,175 C184,185 186,194 187,202 C189,210 192,218 193,225 C194,232 190,237 186,235 C182,233 179,228 178,222 C177,215 176,208 175,202 C172,191 169,180 166,170 C163,158 160,146 157,134 C155,124 151,113 146,107 C147,113 148,119 148,125 C146,138 142,150 139,162 C138,174 141,185 144,196 C147,206 148,214 148,222 C148,232 147,240 146,248 C145,262 144,276 143.5,290 C143,302 141.5,314 140,325 C142,335 143,345 143,356 C143,368 141,380 138,392 C136,402 133,411 131,418 C132,426 134,434 135,441 C135,447 131,451 126,451 C120,451 116,448 116.5,443 C117,436 118,428 119,420 C119,410 120,400 120,392 C119,381 118,370 118,360 C118,349 119,338 120,329 C119,316 118,303 117,290 C116,280 114,270 113,260 C112,253 111,246 110,241 C109,246 108,253 107,260 C106,270 104,280 103,290 C102,303 101,316 100,329 C101,338 102,349 102,360 C102,370 101,381 100,392 C100,400 101,410 101,420 C102,428 103,436 103.5,443 C104,448 100,451 94,451 C89,451 85,447 85,441 C86,434 88,426 89,418 C87,411 84,402 82,392 C79,380 77,368 77,356 C77,345 78,335 80,325 C78.5,314 77,302 76.5,290 C76,276 75,262 74,248 C73,240 72,232 72,222 C72,214 73,206 76,196 C79,185 82,174 81,162 C78,150 74,138 72,125 C72,119 73,113 74,107 C69,113 65,124 63,134 C60,146 57,158 54,170 C51,180 48,191 45,202 C44,208 43,215 42,222 C41,228 38,233 34,235 C30,237 26,232 27,225 C28,218 31,210 33,202 C34,194 36,185 38,175 C41,163 44,151 46,137 C48,124 50,112 52,100 C54,90 59,81 67,75 C77,68 90,63 103,60 C104,57 104,53 103,49 C97,45 93,36 93,25 C93,13 100,6 110,6 Z";

// Front-view right-side bellies
const FP={
  trap:"M117,61 C127,63 138,67 148,74 C144,80 137,83 130,80 C124,77 118,69 117,61 Z",
  delt:"M151,76 C159,76 165,83 167,94 C168,103 165,111 159,113 C153,115 147,110 146,101 C145,91 147,80 151,76 Z",
  pec:"M113,77 C123,76 136,78 143,86 C147,93 147,104 144,113 C139,123 127,129 118,125 C112,122 111,113 111,101 C111,90 112,81 113,77 Z",
  bicep:"M159,116 C165,114 170,120 171,131 C172,143 170,154 166,161 C163,166 158,164 156,157 C154,147 154,131 156,121 C157,117 158,116 159,116 Z",
  forearm:"M167,166 C173,163 178,168 181,177 C184,186 185,194 185,201 C184,207 180,208 177,203 C173,196 170,186 168,178 C167,172 166,168 167,166 Z",
  abs:"M102,132 C105,129 115,129 118,132 C120,146 121,164 120,182 C119,198 116,212 110,220 C104,212 101,198 100,182 C99,164 100,146 102,132 Z",
  oblique:"M122,136 C128,142 133,154 135,168 C137,182 136,196 132,206 C129,212 124,213 123,207 C124,193 124,172 122,152 C122,146 122,140 122,136 Z",
  hipflex:"M115,222 C121,224 128,231 133,241 C136,248 133,253 127,250 C120,246 115,237 113,229 C112,224 113,221 115,222 Z",
  quadLat:"M139,246 C143,252 144,266 143,285 C142,303 139,317 135,326 C131,331 127,328 126,320 C126,303 128,284 131,266 C133,255 136,243 139,246 Z",
  quadRF:"M125,242 C130,240 134,248 134,262 C134,279 133,297 130,310 C128,318 123,321 120,314 C118,303 118,284 119,266 C120,254 122,244 125,242 Z",
  vmo:"M123,296 C126,294 128,300 128,311 C128,322 126,331 122,333 C119,333 118,326 119,316 C120,306 121,299 123,296 Z",
  tib:"M135,348 C139,352 140,364 139,378 C138,392 136,404 132,410 C129,414 126,410 126,402 C127,386 128,368 130,356 C131,349 133,345 135,348 Z",
  calfIn:"M122,352 C126,354 127,364 126,378 C125,390 123,397 120,395 C118,391 118,380 119,368 C120,358 121,351 122,352 Z",
};

// Back-view bellies (trap diamond and erector column are centered, rest right-side)
const BP={
  trap:"M110,58 C121,60 135,64 147,73 C141,82 134,89 127,95 C120,100 115,109 113,120 C112,133 111,144 110,152 C109,144 108,133 107,120 C105,109 100,100 93,95 C86,89 79,82 73,73 C85,64 99,60 110,58 Z",
  delt:"M151,76 C159,76 165,83 167,94 C168,103 165,111 159,113 C153,115 147,110 146,101 C145,91 147,80 151,76 Z",
  infra:"M124,102 C131,102 137,108 138,117 C139,126 134,133 127,134 C120,135 116,129 116,120 C116,111 119,105 124,102 Z",
  lat:"M144,112 C146,120 145,132 142,146 C139,163 133,179 126,192 C121,198 117,197 117,189 C118,174 120,158 124,144 C128,130 133,117 138,110 C140,106 142,107 144,112 Z",
  lowback:"M103,156 C107,153 113,153 117,156 C118,176 118,198 116,214 C114,224 106,224 104,214 C102,198 102,176 103,156 Z",
  tricep:"M159,116 C165,114 170,120 171,131 C172,143 170,154 166,161 C163,166 158,164 156,157 C154,147 154,131 156,121 C157,117 158,116 159,116 Z",
  forearm:"M167,166 C173,163 178,168 181,177 C184,186 185,194 185,201 C184,207 180,208 177,203 C173,196 170,186 168,178 C167,172 166,168 167,166 Z",
  glute:"M116,222 C126,217 136,220 141,230 C146,240 145,256 139,265 C132,274 120,275 115,266 C111,258 111,234 116,222 Z",
  hamOut:"M136,278 C141,283 143,297 142,313 C141,329 137,342 133,348 C129,352 126,348 125,340 C125,323 127,302 130,287 C132,279 134,275 136,278 Z",
  hamIn:"M123,280 C127,279 129,289 129,305 C129,320 127,336 124,344 C121,349 119,345 118,337 C118,322 118,300 120,288 C121,282 122,279 123,280 Z",
  calfLat:"M136,354 C140,358 141,370 140,384 C139,396 136,404 132,405 C129,405 128,400 128,392 C129,378 131,364 133,357 C134,353 135,352 136,354 Z",
  calfMed:"M125,356 C129,355 131,364 131,378 C131,391 129,401 126,405 C122,407 120,402 120,394 C120,381 121,366 122,359 C123,356 124,355 125,356 Z",
};

const FRONT_MUSCLES=[
  {id:"traps_front",d:FP.trap},{id:"traps_front",d:mirX(FP.trap)},
  {id:"front_delt_right",d:FP.delt},{id:"front_delt_left",d:mirX(FP.delt)},
  {id:"chest_right",d:FP.pec},{id:"chest_left",d:mirX(FP.pec)},
  {id:"biceps_right",d:FP.bicep},{id:"biceps_left",d:mirX(FP.bicep)},
  {id:"forearm_right",d:FP.forearm},{id:"forearm_left",d:mirX(FP.forearm)},
  {id:"core",d:FP.abs},
  {id:"obliques_right",d:FP.oblique},{id:"obliques_left",d:mirX(FP.oblique)},
  {id:"hip_right",d:FP.hipflex},{id:"hip_left",d:mirX(FP.hipflex)},
  {id:"quads_right",d:FP.quadLat},{id:"quads_left",d:mirX(FP.quadLat)},
  {id:"quads_right",d:FP.quadRF},{id:"quads_left",d:mirX(FP.quadRF)},
  {id:"vmo_right",d:FP.vmo},{id:"vmo_left",d:mirX(FP.vmo)},
  {id:"calves_right",d:FP.tib},{id:"calves_left",d:mirX(FP.tib)},
  {id:"calves_right",d:FP.calfIn},{id:"calves_left",d:mirX(FP.calfIn)},
];
const BACK_MUSCLES=[
  {id:"traps_back",d:BP.trap},
  {id:"rear_delt_right",d:BP.delt},{id:"rear_delt_left",d:mirX(BP.delt)},
  {id:"infra_right",d:BP.infra},{id:"infra_left",d:mirX(BP.infra)},
  {id:"lats_right",d:BP.lat},{id:"lats_left",d:mirX(BP.lat)},
  {id:"lower_back",d:BP.lowback},
  {id:"triceps_right",d:BP.tricep},{id:"triceps_left",d:mirX(BP.tricep)},
  {id:"forearm_b_right",d:BP.forearm},{id:"forearm_b_left",d:mirX(BP.forearm)},
  {id:"glutes_right",d:BP.glute},{id:"glutes_left",d:mirX(BP.glute)},
  {id:"hamstrings_right",d:BP.hamOut},{id:"hamstrings_left",d:mirX(BP.hamOut)},
  {id:"hamstrings_right",d:BP.hamIn},{id:"hamstrings_left",d:mirX(BP.hamIn)},
  {id:"calves_back_right",d:BP.calfLat},{id:"calves_back_left",d:mirX(BP.calfLat)},
  {id:"calves_back_right",d:BP.calfMed},{id:"calves_back_left",d:mirX(BP.calfMed)},
];
export function MuscleHeatMap({s}){
  const { isDesktop } = useLayout();
  const heat=useMemo(()=>calcMuscleHeat(s.workouts,s.exercises,Math.max(7,parseInt(s.range)||10)),[s.workouts,s.exercises,s.range]);
  const [sel,setSel]=useState(null);
  const [mode,setMode]=useState("activity");

  const muscleIdMap={
    chest_left:"chest",chest_right:"chest",
    front_delt_left:"front_delts",front_delt_right:"front_delts",
    rear_delt_left:"rear_delts",rear_delt_right:"rear_delts",
    biceps_left:"biceps",biceps_right:"biceps",
    triceps_left:"triceps",triceps_right:"triceps",
    // Front forearms share curl/grip volume with biceps; rear forearms with pulling work.
    forearm_left:"biceps",forearm_right:"biceps",
    forearm_b_left:"back",forearm_b_right:"back",
    core:"core",obliques_left:"core",obliques_right:"core",
    hip_left:"quads",hip_right:"quads",
    quads_left:"quads",quads_right:"quads",vmo_left:"quads",vmo_right:"quads",
    calves_left:"calves",calves_right:"calves",
    calves_back_left:"calves",calves_back_right:"calves",
    traps_front:"traps",traps_back:"traps",
    lats_left:"back",lats_right:"back",infra_left:"back",infra_right:"back",lower_back:"back",
    glutes_left:"glutes",glutes_right:"glutes",
    hamstrings_left:"hamstrings",hamstrings_right:"hamstrings",
  };

  const getScore=(id)=>{
    const key=muscleIdMap[id]||id;
    let v=heat[key]||0;
    // Side delts have no dedicated SVG region — surface lateral-raise volume on the
    // front-delt shape so it isn't invisible on the figure (still listed separately below).
    if(key==="front_delts")v+=heat.side_delts||0;
    return v;
  };
  const getMuscleId=(svgId)=>muscleIdMap[svgId]||svgId;

  const UNTRAINED="rgba(140,150,162,0.35)";
  const heatColor=(score)=>{
    if(mode==="recovery"){
      if(score<=0)return UNTRAINED;
      if(score<=3)return"rgba(248,81,73,0.85)";
      if(score<=6)return"rgba(251,146,60,0.8)";
      return"rgba(0,245,160,0.55)";
    }
    if(score<=0)return UNTRAINED;
    if(score<=2)return"rgba(250,204,21,0.45)";
    if(score<=4)return"rgba(250,204,21,0.78)";
    if(score<=6)return"rgba(251,146,60,0.85)";
    if(score<=8)return"rgba(248,81,73,0.85)";
    return"rgba(239,68,68,0.95)";
  };

  const glowFilter=(score)=>{
    if(score<=1)return"none";
    const c=score>7?"rgba(239,68,68,0.5)":score>4?"rgba(251,146,60,0.4)":"rgba(250,204,21,0.3)";
    return`drop-shadow(0 0 ${Math.min(score*1.6,13)}px ${c})`;
  };

  const M=({id,d})=>{
    const score=getScore(id);
    const muscleId=getMuscleId(id);
    const selected=sel===muscleId;
    return(
      <path d={d} fill={heatColor(score)}
        stroke={selected?V.accent:"#05080b"} strokeWidth={selected?1.6:1}
        style={{cursor:"pointer",transition:"fill .28s,filter .2s",filter:glowFilter(score)}}
        onClick={()=>setSel(sel===muscleId?null:muscleId)}/>
    );
  };

  const selDetail=useMemo(()=>{
    if(!sel)return null;
    const score=(heat[sel]||0).toFixed(1);
    const exIds=Object.entries(MUSCLE_MAP).filter(([eid,m])=>m.primary?.includes(sel)||m.secondary?.includes(sel)).map(([eid])=>eid);
    const recentW=s.workouts.slice(0,14);
    const exercises=[];let lastDate=null;
    recentW.forEach(w=>{
      w.exercises.forEach(ex=>{
        if(exIds.includes(ex.exerciseId)){
          const name=s.exercises.find(e=>e.id===ex.exerciseId)?.name||ex.exerciseId;
          const existing=exercises.find(e=>e.name===name);
          if(existing)existing.sets+=ex.sets.length;
          else exercises.push({name,sets:ex.sets.length});
          if(!lastDate||w.date>lastDate)lastDate=w.date;
        }
      });
    });
    const daysSince=lastDate?Math.floor((new Date()-new Date(lastDate+"T12:00:00"))/86400000):null;
    return{score,exercises:exercises.sort((a,b)=>b.sets-a.sets).slice(0,5),lastDate,daysSince};
  },[sel,heat,s.workouts,s.exercises]);

  const imbalances=useMemo(()=>{
    const msgs=[];
    const pairs=[["quads","hamstrings","Quad-to-hamstring"],["chest","back","Chest-to-back"],["biceps","triceps","Bicep-to-tricep"],["front_delts","rear_delts","Front-to-rear delt"]];
    pairs.forEach(([a,b,label])=>{
      if(heat[a]>0&&heat[b]<=0.5)msgs.push({msg:`${MUSCLE_LABELS[a]} trained but ${MUSCLE_LABELS[b]} neglected`,fix:b});
      else if(heat[a]>heat[b]*2.5&&heat[b]>0)msgs.push({msg:`${label} imbalance`,fix:b});
    });
    if(heat.calves<=0.5&&heat.quads>3)msgs.push({msg:"Calves untrained — add calf raises",fix:"calves"});
    if(heat.core<=0.5&&s.workouts.length>3)msgs.push({msg:"Core neglected — add ab work",fix:"core"});
    return msgs;
  },[heat,s.workouts.length]);

  return(
    <div style={{display:"flex",flexDirection:"column",gap:10,maxWidth:isDesktop?900:"100%",margin:isDesktop?"0 auto":0}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
        <div>
          <div style={{fontSize:16,fontWeight:800,color:V.text}}>Muscle Map</div>
          <div style={{fontSize:11,color:V.text3}}>Last {Math.max(7,parseInt(s.range)||10)} days · Tap a muscle for details</div>
        </div>
        <div style={{display:"flex",gap:4}}>
          {["activity","recovery"].map(m=>(
            <button key={m} onClick={()=>setMode(m)} style={{padding:"5px 10px",borderRadius:6,
              border:`1.5px solid ${mode===m?V.accent:V.cardBorder}`,
              background:mode===m?`${V.accent}10`:"transparent",
              cursor:"pointer",fontSize:11,fontWeight:700,color:mode===m?V.accent:V.text3,
              fontFamily:V.font,textTransform:"capitalize"}}>{m}</button>
          ))}
        </div>
      </div>

      <Card style={{padding:16,background:V.mode==="dark"?"#05080b":V.card}}>
        <div style={{display:"flex",gap:14,maxWidth:430,margin:"0 auto"}}>
          {[{label:"FRONT",muscles:FRONT_MUSCLES},{label:"BACK",muscles:BACK_MUSCLES}].map(f=>(
            <div key={f.label} style={{flex:1,minWidth:0}}>
              <svg viewBox="15 0 190 462" style={{width:"100%",display:"block"}}>
                <path d={BODY_OUTLINE} fill={V.mode==="dark"?"#10151c":"#e2e8f0"} stroke={V.mode==="dark"?"#7e8b99":"#64748b"} strokeWidth="1.5"/>
                {f.muscles.map((m,i)=><M key={i} id={m.id} d={m.d}/>)}
                {f.label==="FRONT"?(
                  <g style={{pointerEvents:"none"}}>
                    {/* Ab inscriptions + linea alba */}
                    {[148,164,180,196].map(y=><path key={y} d={`M101,${y} C106,${y-2} 114,${y-2} 119,${y}`} fill="none" stroke={V.mode==="dark"?"#05080b":"#f8fafc"} strokeWidth="1.4"/>)}
                    <line x1="110" y1="131" x2="110" y2="218" stroke={V.mode==="dark"?"#05080b":"#f8fafc"} strokeWidth="1.4"/>
                    {/* Clavicles */}
                    <path d="M110,72 C103,70 92,69 82,74 M110,72 C117,70 128,69 138,74" fill="none" stroke={V.mode==="dark"?"#7e8b99":"#64748b"} strokeWidth="0.8" opacity="0.35"/>
                    {/* Patellas */}
                    <ellipse cx="90" cy="334" rx="6" ry="6.5" fill="none" stroke={V.mode==="dark"?"#7e8b99":"#64748b"} strokeWidth="0.7" opacity="0.3"/>
                    <ellipse cx="130" cy="334" rx="6" ry="6.5" fill="none" stroke={V.mode==="dark"?"#7e8b99":"#64748b"} strokeWidth="0.7" opacity="0.3"/>
                  </g>
                ):(
                  <g style={{pointerEvents:"none"}}>
                    {/* Spine */}
                    <line x1="110" y1="156" x2="110" y2="222" stroke={V.mode==="dark"?"#05080b":"#f8fafc"} strokeWidth="1.4"/>
                    {/* Neck columns */}
                    <path d="M104,46 L104,60 M116,46 L116,60" stroke={V.mode==="dark"?"#7e8b99":"#64748b"} strokeWidth="0.7" opacity="0.3" fill="none"/>
                    {/* Achilles tendons */}
                    <path d="M90,410 L90,432 M130,410 L130,432" stroke={V.mode==="dark"?"#7e8b99":"#64748b"} strokeWidth="0.7" opacity="0.25" fill="none"/>
                  </g>
                )}
              </svg>
              <div style={{textAlign:"center",fontSize:11,fontWeight:700,letterSpacing:1.5,color:V.text3,marginTop:6}}>{f.label}</div>
            </div>
          ))}
        </div>

        {/* Legend */}
        <div style={{display:"flex",justifyContent:"center",flexWrap:"wrap",gap:12,marginTop:12}}>
          {(mode==="activity"?
            [{l:"Untrained",c:"rgba(140,150,162,0.45)"},{l:"Light",c:"rgba(250,204,21,0.75)"},{l:"Moderate",c:"rgba(251,146,60,0.85)"},{l:"High",c:"rgba(239,68,68,0.9)"}]
            :[{l:"Recovered",c:"rgba(0,245,160,0.55)"},{l:"Recovering",c:"rgba(251,146,60,0.8)"},{l:"Fatigued",c:"rgba(248,81,73,0.85)"},{l:"Untrained",c:"rgba(140,150,162,0.45)"}]
          ).map(x=>(
            <div key={x.l} style={{display:"flex",alignItems:"center",gap:4}}>
              <div style={{width:10,height:10,borderRadius:"50%",background:x.c}}/>
              <span style={{fontSize:11,color:V.text3}}>{x.l}</span>
            </div>
          ))}
        </div>
      </Card>

      {/* Selected muscle detail */}
      {sel&&selDetail&&(
        <Card glow style={{padding:14}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
            <div style={{fontSize:15,fontWeight:800,color:V.text}}>{MUSCLE_LABELS[sel]}</div>
            <button onClick={()=>setSel(null)} style={{background:"none",border:"none",cursor:"pointer",
              padding:4,fontSize:12,color:V.text3}}>✕</button>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,marginBottom:10}}>
            <div style={{textAlign:"center",padding:"8px 0",borderRadius:8,background:V.card}}>
              <div style={{fontSize:18,fontWeight:800,color:parseFloat(selDetail.score)>7?V.danger:parseFloat(selDetail.score)>4?V.accent:V.warn,fontFamily:V.mono}}>{selDetail.score}</div>
              <div style={{fontSize:11,color:V.text3,fontWeight:600}}>WORKLOAD</div>
            </div>
            <div style={{textAlign:"center",padding:"8px 0",borderRadius:8,background:V.card}}>
              <div style={{fontSize:14,fontWeight:800,color:V.accent2,fontFamily:V.mono}}>{selDetail.daysSince!==null?`${selDetail.daysSince}d`:"—"}</div>
              <div style={{fontSize:11,color:V.text3,fontWeight:600}}>LAST HIT</div>
            </div>
            <div style={{textAlign:"center",padding:"8px 0",borderRadius:8,background:V.card}}>
              <div style={{fontSize:14,fontWeight:800,color:V.purple,fontFamily:V.mono}}>{selDetail.exercises.length}</div>
              <div style={{fontSize:11,color:V.text3,fontWeight:600}}>EXERCISES</div>
            </div>
          </div>
          {selDetail.exercises.length>0?(
            <div>
              <div style={{fontSize:11,color:V.text3,fontWeight:600,marginBottom:4}}>Recent Exercises</div>
              {selDetail.exercises.map((ex,i)=>(
                <div key={i} style={{display:"flex",justifyContent:"space-between",padding:"5px 0",
                  borderBottom:i<selDetail.exercises.length-1?`1px solid ${V.divider}`:"none"}}>
                  <span style={{fontSize:12,color:V.text}}>{ex.name}</span>
                  <span style={{fontSize:11,color:V.accent,fontFamily:V.mono}}>{ex.sets} sets</span>
                </div>
              ))}
            </div>
          ):(
            <div style={{fontSize:11,color:V.text3,textAlign:"center",padding:8}}>No recent exercises for this muscle</div>
          )}
        </Card>
      )}

      {/* Ranked muscle list */}
      <Card style={{padding:12}}>
        <span style={{fontSize:12,color:V.text3,fontWeight:600,display:"block",marginBottom:8}}>Muscle Rankings</span>
        {[...ALL_MUSCLES].sort((a,b)=>(heat[b]||0)-(heat[a]||0)).map((m,i)=>{
          const score=heat[m]||0;
          return(
            <div key={m} onClick={()=>setSel(sel===m?null:m)} style={{display:"flex",alignItems:"center",gap:8,padding:"6px 8px",
              background:sel===m?`${V.accent}08`:"transparent",borderRadius:6,cursor:"pointer",
              borderBottom:i<ALL_MUSCLES.length-1?`1px solid ${V.divider}`:"none"}}>
              <div style={{width:24,height:4,borderRadius:2,background:V.hoverBg,overflow:"hidden",flexShrink:0}}>
                <div style={{width:`${Math.min(score/10*100,100)}%`,height:"100%",borderRadius:2,
                  background:score>7?V.danger:score>4?V.accent:score>1?V.warn:"transparent",transition:"width .3s"}}/>
              </div>
              <span style={{fontSize:11,color:V.text,flex:1,fontWeight:sel===m?700:400}}>{MUSCLE_LABELS[m]}</span>
              <span style={{fontSize:11,color:score>7?V.danger:score>4?V.accent:score>0?V.warn:V.text3,
                fontFamily:V.mono,fontWeight:700}}>{score.toFixed(1)}</span>
            </div>
          );
        })}
      </Card>

      {/* Imbalance Alerts */}
      {imbalances.length>0&&(
        <Card style={{padding:12}}>
          <div style={{display:"flex",alignItems:"center",gap:5,marginBottom:8}}>
            {Icons.zap({size:13,color:V.warn})}
            <span style={{fontSize:12,color:V.warn,fontWeight:700}}>Imbalance Alerts</span>
          </div>
          {imbalances.map((im,i)=>(
            <div key={i} style={{fontSize:11,color:V.text2,padding:"6px 0",borderBottom:i<imbalances.length-1?`1px solid ${V.divider}`:"none"}}>
              {im.msg}
            </div>
          ))}
        </Card>
      )}
    </div>
  );
}

// ─── Strength Score System ───
export function calcStrengthScore(workouts,bodyWeight,seed,units="lbs"){
  const bw=parseFloat(bodyWeight)||(units==="kg"?80:180);
  // seed: lifts entered during onboarding (already in display units) — a baseline until logged sets beat it
  const lifts={bench:seed?.bench||0,squat:seed?.squat||0,deadlift:seed?.deadlift||0};

  // Find best e1RM for each lift
  workouts.forEach(w=>{
    w.exercises.forEach(ex=>{
      if(Object.prototype.hasOwnProperty.call(lifts,ex.exerciseId)){
        ex.sets.forEach(st=>{
          const wt=parseFloat(st.weight)||0;
          const rp=parseInt(st.reps)||0;
          if(wt>0&&rp>0){
            const e1rm=Math.round(wt*(1+rp/30));
            lifts[ex.exerciseId]=Math.max(lifts[ex.exerciseId],e1rm);
          }
        });
      }
    });
  });

  const benchRatio=lifts.bench/bw;
  const squatRatio=lifts.squat/bw;
  const deadRatio=lifts.deadlift/bw;
  const score=benchRatio*0.30+squatRatio*0.35+deadRatio*0.35;

  let rank,rankColor;
  if(score<0.5){rank="Novice";rankColor=V.text3;}
  else if(score<1.0){rank="Beginner";rankColor=V.warn;}
  else if(score<1.5){rank="Intermediate";rankColor=V.accent2;}
  else if(score<2.0){rank="Advanced";rankColor=V.accent;}
  else{rank="Elite";rankColor=V.purple;}

  // Badges
  const badges=[];
  if(benchRatio>=1.5)badges.push({label:"Bench 1.5x BW",icon:"🏋️"});
  if(benchRatio>=2.0)badges.push({label:"Bench 2x BW",icon:"💎"});
  if(squatRatio>=2.0)badges.push({label:"Squat 2x BW",icon:"🦵"});
  if(squatRatio>=2.5)badges.push({label:"Squat 2.5x BW",icon:"👑"});
  if(deadRatio>=2.5)badges.push({label:"Dead 2.5x BW",icon:"🔥"});
  if(deadRatio>=3.0)badges.push({label:"Dead 3x BW",icon:"⚡"});
  if(score>=1.0)badges.push({label:"1000lb Club",icon:"🏆",cond:(lifts.bench+lifts.squat+lifts.deadlift)*(units==="kg"?2.20462:1)>=1000});

  return{score:Math.round(score*100)/100,rank,rankColor,lifts,benchRatio:Math.round(benchRatio*100)/100,
    squatRatio:Math.round(squatRatio*100)/100,deadRatio:Math.round(deadRatio*100)/100,
    badges:badges.filter(b=>b.cond===undefined||b.cond),total:lifts.bench+lifts.squat+lifts.deadlift,bw};
}

export function StrengthScoreCard({s}){
  const { isDesktop } = useLayout();
  const bw=s.body.find(b=>b.weight)?.weight||s.goals.goalWeight||(s.units==="kg"?80:180);
  const seed=useMemo(()=>liftsIn(s.profile?.lifts,s.units),[s.profile?.lifts,s.units]);
  const data=useMemo(()=>calcStrengthScore(s.workouts,bw,seed,s.units),[s.workouts,bw,seed,s.units]);
  const u=wUnit(s.units);

  // Monthly trend
  const trend=useMemo(()=>{
    const months={};
    s.workouts.forEach(w=>{
      const mo=w.date.slice(0,7);
      if(!months[mo])months[mo]=[];
      months[mo].push(w);
    });
    // Score at the END of each month = best lifts logged up to then (a month without
    // bench work no longer makes the score "drop")
    return Object.entries(months).sort((a,b)=>a[0].localeCompare(b[0])).slice(-6).map(([mo])=>{
      const sc=calcStrengthScore(s.workouts.filter(w=>w.date.slice(0,7)<=mo),bw,seed,s.units);
      return{date:mo.slice(5),score:sc.score};
    });
  },[s.workouts,bw,seed,s.units]);

  // Score ring
  const pct=Math.min(100,data.score/2.5*100);
  const r=44,circ=2*Math.PI*r,offset=circ*(1-pct/100);

  return(
    <div style={{display:"flex",flexDirection:"column",gap:10,maxWidth:isDesktop?900:"100%",margin:isDesktop?"0 auto":0}}>
      {/* Main Score */}
      <Card style={{padding:16,textAlign:"center"}}>
        <svg width={100} height={100} style={{display:"block",margin:"0 auto 8px"}}>
          <circle cx={50} cy={50} r={r} fill="none" stroke={V.divider} strokeWidth={6}/>
          <circle cx={50} cy={50} r={r} fill="none" stroke={data.rankColor} strokeWidth={6}
            strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round"
            transform="rotate(-90 50 50)" style={{transition:"stroke-dashoffset 1s ease"}}/>
          <text x={50} y={44} textAnchor="middle" fill={V.text} fontSize={22} fontWeight={800} fontFamily={V.mono}>{data.score.toFixed(2)}</text>
          <text x={50} y={62} textAnchor="middle" fill={data.rankColor} fontSize={10} fontWeight={700}>{data.rank}</text>
        </svg>

        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,marginTop:4}}>
          {[{l:"Bench",v:data.lifts.bench,r:data.benchRatio,c:V.accent},
            {l:"Squat",v:data.lifts.squat,r:data.squatRatio,c:V.accent2},
            {l:"Dead",v:data.lifts.deadlift,r:data.deadRatio,c:V.purple}].map(x=>(
            <div key={x.l} style={{padding:"8px 4px",background:`${x.c}08`,borderRadius:8}}>
              <div style={{fontSize:16,fontWeight:800,color:x.c,fontFamily:V.mono}}>{x.v||"--"}</div>
              <div style={{fontSize:11,color:V.text3,textTransform:"uppercase",marginTop:1}}>{x.l} E1RM</div>
              <div style={{fontSize:11,color:x.c,fontFamily:V.mono,marginTop:2}}>{x.r}x BW</div>
            </div>
          ))}
        </div>

        <div style={{display:"flex",justifyContent:"center",gap:12,marginTop:10}}>
          <span style={{fontSize:11,color:V.text3}}>Total: <span style={{color:V.text,fontWeight:700,fontFamily:V.mono}}>{data.total} {u}</span></span>
          <span style={{fontSize:11,color:V.text3}}>BW: <span style={{color:V.text,fontWeight:700,fontFamily:V.mono}}>{bw} {u}</span></span>
        </div>
      </Card>

      {/* Rank Scale */}
      <Card style={{padding:12}}>
        <span style={{fontSize:12,color:V.text3,fontWeight:600,display:"block",marginBottom:8}}>Rank Scale</span>
        <div style={{display:"flex",gap:4,marginBottom:4}}>
          {[{l:"Novice",min:0,max:0.5,c:V.text3},{l:"Beginner",min:0.5,max:1.0,c:V.warn},
            {l:"Inter",min:1.0,max:1.5,c:V.accent2},{l:"Advanced",min:1.5,max:2.0,c:V.accent},
            {l:"Elite",min:2.0,max:2.5,c:V.purple}].map(tier=>(
            <div key={tier.l} style={{flex:1,textAlign:"center"}}>
              <div style={{height:6,borderRadius:3,marginBottom:4,
                background:data.score>=tier.min?tier.c:V.inputBg}}/>
              <span style={{fontSize:11,color:data.score>=tier.min?tier.c:V.text3,fontWeight:600}}>{tier.l}</span>
            </div>
          ))}
        </div>
      </Card>

      {/* Badges */}
      {data.badges.length>0&&(
        <Card style={{padding:12}}>
          <span style={{fontSize:12,color:V.text3,fontWeight:600,display:"block",marginBottom:8}}>Badges Earned</span>
          <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
            {data.badges.map((b,i)=>(
              <div key={i} style={{display:"flex",alignItems:"center",gap:4,padding:"5px 10px",
                background:`${V.accent}10`,borderRadius:8,border:`1px solid ${V.accent}20`}}>
                <span>{b.icon}</span>
                <span style={{fontSize:11,color:V.accent,fontWeight:600}}>{b.label}</span>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* #24: Individual Lift Standards */}
      <Card style={{padding:12}}>
        <span style={{fontSize:12,color:V.text3,fontWeight:600,display:"block",marginBottom:8}}>Lift Standards</span>
        <div style={{fontSize:11,color:V.text3,marginBottom:10}}>Based on {bw} {u} body weight · ratios from strength standards</div>
        {[{l:"Bench Press",id:"bench",ratios:[0.5,0.75,1.0,1.25,1.5]},
          {l:"Squat",id:"squat",ratios:[0.75,1.0,1.25,1.75,2.25]},
          {l:"Deadlift",id:"deadlift",ratios:[1.0,1.25,1.5,2.0,2.5]},
          {l:"OHP",id:"ohp",ratios:[0.35,0.45,0.6,0.75,0.9]}].map(lift=>{
          const best=(()=>{let b=0;s.workouts.forEach(w=>w.exercises.forEach(e=>{if(e.exerciseId===lift.id)e.sets.forEach(st=>{
            const e1=calc1RM(parseFloat(st.weight)||0,parseInt(st.reps)||0);if(e1>b)b=e1;});}));return b;})();
          const ratio=best>0?best/parseFloat(bw):0;
          const levels=["Beginner","Novice","Intermediate","Advanced","Elite"];
          const colors=[V.text3,V.warn,V.accent2,V.accent,V.purple];
          let level=0;
          for(let i=0;i<lift.ratios.length;i++){if(ratio>=lift.ratios[i])level=i;}
          const nextRatio=level<4?lift.ratios[level+1]:null;
          const nextWeight=nextRatio?Math.round(nextRatio*parseFloat(bw)):null;
          return(
            <div key={lift.id} style={{marginBottom:10,paddingBottom:10,borderBottom:`1px solid ${V.divider}`}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:4}}>
                <span style={{fontSize:12,fontWeight:600,color:V.text}}>{lift.l}</span>
                <div style={{display:"flex",alignItems:"center",gap:6}}>
                  <span style={{fontSize:14,fontWeight:800,color:colors[level],fontFamily:V.mono}}>{best||"--"}</span>
                  <span style={{padding:"2px 6px",borderRadius:4,background:`${colors[level]}15`,fontSize:11,fontWeight:700,color:colors[level]}}>{levels[level]}</span>
                </div>
              </div>
              <div style={{display:"flex",gap:2}}>
                {levels.map((lv,li)=>(
                  <div key={li} style={{flex:1,height:4,borderRadius:2,background:li<=level?colors[li]:V.inputBg}}/>
                ))}
              </div>
              {nextWeight&&best>0&&<div style={{fontSize:11,color:V.text3,marginTop:3}}>{nextWeight-best} {u} to {levels[level+1]} ({nextWeight} {u})</div>}
            </div>
          );
        })}
      </Card>

      {/* Trend */}
      {trend.length>1&&(
        <Card style={{padding:12}}>
          <span style={{fontSize:12,color:V.text3,fontWeight:600,display:"block",marginBottom:8}}>Score Trend</span>
          <ResponsiveContainer width="100%" height={100}>
            <AreaChart data={trend}>
              <defs><linearGradient id="ssGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={V.accent} stopOpacity={0.2}/>
                <stop offset="100%" stopColor={V.accent} stopOpacity={0}/>
              </linearGradient></defs>
              <XAxis dataKey="date" {...chartCfg.axis} interval="preserveStartEnd"/>
              <YAxis {...chartCfg.axis} width={26} domain={[0,'dataMax+0.3']}/>
              <Tooltip {...chartCfg.tip}/>
              <Area type="monotone" dataKey="score" stroke={V.accent} strokeWidth={2} fill="url(#ssGrad)"/>
            </AreaChart>
          </ResponsiveContainer>
        </Card>
      )}
    </div>
  );
}

// ─── Log Hub: Central action screen ───
// ─── #2 Goal Engine + Milestones ───

export function AnalyticsTab({s,d}){
  const { isDesktop } = useLayout();
  const [selEx,setSelEx]=useState("bench");

  const selIsCardio=isCardio(selEx,s.exercises);
  const exProg=useMemo(()=>{
    let runMax=0;
    return s.workouts.filter(w=>w.date>=ago(s.range)).sort((a,b)=>a.date.localeCompare(b.date)).reduce((arr,w)=>{
      const ex=w.exercises.find(e=>e.exerciseId===selEx);
      if(ex){
        if(selIsCardio){
          const totalDur=ex.sets.reduce((s,st)=>s+(parseFloat(st.duration)||0),0);
          const totalDist=ex.sets.reduce((s,st)=>s+(parseFloat(st.distance)||0),0);
          const isPRPoint=totalDist>runMax;if(totalDist>runMax)runMax=totalDist;
          arr.push({date:fmtShort(w.date),max:totalDist,vol:totalDur,e1rm:0,isPR:isPRPoint});
        }else{
          const wts=ex.sets.map(st=>parseFloat(st.weight)||0);
          const mw=wts.length?Math.max(...wts):0; // Math.max() of an empty list is -Infinity
          const vol=ex.sets.reduce((s,st)=>s+(parseFloat(st.weight)||0)*(parseInt(st.reps)||0),0);
          const e1rm=wts.length?Math.max(...ex.sets.map(st=>(parseFloat(st.weight)||0)*(1+(parseInt(st.reps)||0)/30))):0;
          const isPRPoint=mw>runMax;if(mw>runMax)runMax=mw;
          arr.push({date:fmtShort(w.date),max:mw,vol,e1rm:Math.round(e1rm),isPR:isPRPoint});
        }
      }
      return arr;
    },[]);
  },[s.workouts,selEx,s.range,selIsCardio]);

  const volByGroup=useMemo(()=>{
    const g={};
    s.workouts.filter(w=>w.date>=ago(s.range)).forEach(w=>w.exercises.forEach(ex=>{
      const info=s.exercises.find(e=>e.id===ex.exerciseId);
      if(info){const c=info.cat==="Cardio";const v=c?ex.sets.reduce((s,st)=>s+(parseFloat(st.duration)||0),0):ex.sets.reduce((s,st)=>s+(parseFloat(st.weight)||0)*(parseInt(st.reps)||0),0);g[info.cat]=(g[info.cat]||0)+v;}
    }));
    return Object.entries(g).map(([name,value])=>({name,value}));
  },[s.workouts,s.exercises,s.range]);

  const freq=useMemo(()=>{
    const c={};
    s.workouts.filter(w=>w.date>=ago(s.range)).forEach(w=>{const dy=new Date(w.date+"T12:00:00").getDay();c[dy]=(c[dy]||0)+1;});
    return["S","M","T","W","T","F","S"].map((d,i)=>({d,n:c[i]||0}));
  },[s.workouts,s.range]);

  const maxF=Math.max(...freq.map(f=>f.n),1);
  const colors=[V.accent,V.accent2,V.purple,V.warn,V.danger,"#ffd93d"];
  const exOpts=useMemo(()=>{
    const freq={};
    s.workouts.forEach(w=>w.exercises.forEach(e=>{freq[e.exerciseId]=(freq[e.exerciseId]||0)+1;}));
    return Object.entries(freq).sort((a,b)=>b[1]-a[1]).slice(0,12).map(([id])=>id);
  },[s.workouts]);

  return(
    <div style={{display:"flex",flexDirection:"column",gap:12,maxWidth:isDesktop?1200:"100%",margin:isDesktop?"0 auto":0}}>
      {/* Exercise Selector */}
      <Card>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
          <span style={{fontSize:13,color:V.text2,fontWeight:600}}>Exercise Progression</span>
        </div>
        <div style={{display:"flex",gap:6,overflowX:"auto",marginBottom:14,paddingBottom:4,WebkitOverflowScrolling:"touch",scrollbarWidth:"none"}}>
          {exOpts.map(id=>(
            <Chip key={id} label={s.exercises.find(e=>e.id===id)?.name||id} active={selEx===id} onClick={()=>setSelEx(id)}/>
          ))}
        </div>
        <span style={{fontSize:11,color:V.text3,display:"block",marginBottom:6}}>{selIsCardio?`Distance (${dUnit(s.units)}) & Duration`:"Max Weight & Est. 1RM"}</span>
        {exProg.length===0?<EmptyState icon={Icons.chart} color={V.purple} title="Nothing logged in this range" action="Log a workout" onAction={()=>d({type:"TAB",tab:"log_workout"})}>Pick another exercise above or widen the range with the 7d / 14d / 30d chips.</EmptyState>:
        <ResponsiveContainer width="100%" height={180}>
          <LineChart data={exProg}>
            <CartesianGrid {...chartCfg.grid} vertical={false}/>
            <XAxis dataKey="date" {...chartCfg.axis} interval="preserveStartEnd"/>
            <YAxis {...chartCfg.axis} width={30}/>
            <Tooltip {...chartCfg.tip}/>
            <Line type="monotone" dataKey="max" stroke={V.accent} strokeWidth={2} dot={{r:3}} name={selIsCardio?"Distance":"Max"}/>
            {!selIsCardio&&<Line type="monotone" dataKey="e1rm" stroke={V.purple} strokeWidth={2} strokeDasharray="5 5" dot={{r:2}} name="E1RM"/>}
            {exProg.filter(p=>p.isPR).map((p,i)=>(
              <ReferenceDot key={i} x={p.date} y={p.max} r={6}
                fill="#f59e0b" stroke={V.bg} strokeWidth={2}
                label={{value:"PR",position:"top",fill:"#f59e0b",fontSize:11,fontWeight:800}}/>
            ))}
          </LineChart>
        </ResponsiveContainer>}
      </Card>

      {/* Volume Pie */}
      {volByGroup.length>0&&<Card>
        <span style={{fontSize:11,color:V.text3,fontWeight:700,display:"block",marginBottom:12,textTransform:"uppercase",letterSpacing:".06em"}}>Volume by Muscle</span>
        <ResponsiveContainer width="100%" height={200}>
          <PieChart>
            <Pie data={volByGroup} cx="50%" cy="50%" innerRadius={50} outerRadius={75} dataKey="value" paddingAngle={3}>
              {volByGroup.map((_,i)=><Cell key={i} fill={colors[i%colors.length]}/>)}
            </Pie>
            <Tooltip {...chartCfg.tip}/>
          </PieChart>
        </ResponsiveContainer>
        <div style={{display:"flex",flexWrap:"wrap",gap:6,justifyContent:"center",padding:"10px 4px 2px"}}>
          {volByGroup.map((g,i)=>(
            <span key={g.name} style={{fontSize:11,color:colors[i%colors.length],display:"flex",alignItems:"center",gap:5,fontWeight:600,
              padding:"3px 8px",borderRadius:6,background:`${colors[i%colors.length]}10`}}>
              <span style={{width:8,height:8,borderRadius:2,flexShrink:0,background:colors[i%colors.length]}}/>{g.name}
            </span>
          ))}
        </div>
      </Card>}

      {/* Frequency */}
      <Card>
        <span style={{fontSize:13,color:V.text2,fontWeight:600,display:"block",marginBottom:14}}>Weekly Frequency</span>
        <div style={{display:"flex",gap:8,justifyContent:"center",padding:"8px 0"}}>
          {freq.map((f,i)=>(
            <div key={i} style={{display:"flex",flexDirection:"column",alignItems:"center",gap:5,flex:1}}>
              <div style={{width:"100%",maxWidth:32,height:80,borderRadius:8,background:V.card,
                position:"relative",overflow:"hidden",display:"flex",alignItems:"flex-end"}}>
                <div style={{width:"100%",height:`${(f.n/maxF)*100}%`,background:`linear-gradient(to top,${V.accent}40,${V.accent2}40)`,
                  borderRadius:6,transition:"height .4s ease",minHeight:f.n>0?6:0}}/>
              </div>
              <span style={{fontSize:11,color:V.text3,fontWeight:600}}>{f.d}</span>
              <span style={{fontSize:11,color:f.n>0?V.accent:V.text3,fontFamily:V.mono,fontWeight:700}}>{f.n}</span>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

// ═══════════════════════════════════════
//  CALENDAR TAB
// ═══════════════════════════════════════
export const WORKOUT_TYPES = ["Rest","Chest","Back","Legs","Shoulders","Arms","Core","Cardio","Push","Pull","Upper","Lower","Full Body","Custom"];
// Getters so theme tokens are read at render time — a module-level literal kept the
// load-time theme's colours after a light/dark toggle.
export const typeColors = {
  "Rest":"#94a3b8",get Chest(){return V.accent;},get Back(){return V.accent2;},get Legs(){return V.purple;},get Shoulders(){return V.warn;},
  "Arms":"#ff6b9d","Core":"#eab308","Cardio":"#ff6b6b",get Push(){return V.accent;},get Pull(){return V.accent2;},
  "Upper":"#e879f9","Lower":"#fb923c","Full Body":"#34d399","Custom":"#94a3b8",
};
const WORKOUT_TYPES_COLORS = typeColors;

